////Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/crm/leads/table-components/data-table-row-actions.tsx


"use client";

import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Row } from "@tanstack/react-table";
import { useState } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AlertModal from "@/components/modals/alert-modal";
import RightViewModalNoTrigger from "@/components/modals/right-view-notrigger";
import { UpdateLeadForm } from "../components/UpdateLeadForm";
import { leadSchema } from "../table-data/schema";

interface LeadData {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  status?: string | null; // Allow null by adding "| null"

  createdAt: Date;
  updatedAt: Date;
}

interface DataTableRowActionsProps<TData> {
  row: Row<TData & LeadData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const router = useRouter();
  const lead = leadSchema.parse(row.original);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const { toast } = useToast();

  const onDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`/api/crm/leads/${lead?.id}`);
      toast({
        title: "Success",
        description: "Lead has been deleted",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong while deleting lead. Please try again.",
      });
    } finally {
      setLoading(false);
      setOpen(false);
      router.refresh();
    }
  };

  const onHideToggle = async () => {
    try {
      const response = await axios.put(`/api/crm/leads/hide/${lead?.id}`);
      toast({
        title: "Success",
        description: response.data.message,
      });
      router.refresh();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong while toggling visibility. Please try again.",
      });
    }
  };

  return (
    <>
      <AlertModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={onDelete}
        loading={loading}
      />

      <RightViewModalNoTrigger
        title={"Update investor profile" + " - " + lead?.firstName + " " + lead?.lastName}
        description="Update investor profile details"
        open={updateOpen}
        setOpen={setUpdateOpen}
      >
        <UpdateLeadForm initialData={row.original} setOpen={setUpdateOpen} />
      </RightViewModalNoTrigger>

      <div className="flex items-center space-x-2">
        <Button 
          variant="outline" 
          onClick={() => router.push(`/crm/leads/${lead?.id}`)}
        >
          View
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
              <DotsHorizontalIcon className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuItem onClick={() => setUpdateOpen(true)}>
              Update
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onHideToggle}>
              {row.original.status === "HIDDEN" ? "Unhide" : "Hide"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setOpen(true)}>
              Delete
              <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}